// Ortak attachment yükleyici — presigned upload + scan_status workflow.
// İki tüketici:
//   - FileRenderer (type:"file"): syncValue verir → cevap değeri = clean attachment_id'ler.
//   - EvidenceUpload (allowEvidence, non-file): syncValue YOK → sadece kanıt belgesi,
//     cevap değerine dokunmaz.
// Backend endpoint'leri (presign/commit/list/delete) questionCode bazlı, tip-bağımsız
// (attachments.ts: type==="file" VEYA allowEvidence olan her soruya izin verir).
// TODO-EVIDENCE-01: dosya evidence havuzunda (KPI panelinde "form kanıtı" rozetiyle de görünür);
// TODO-EVIDENCE-05: `error` tarama durumu (cron yeniden dener) + `purgedAt` (saklama süresi doldu).

import { useEffect, useRef, useState } from "react";
import type {
  AttachmentInfo,
  AttachmentListResponse,
  AttachmentPresignRequest,
  AttachmentPresignResponse,
  QuestionSnapshot,
} from "@mahir/shared";
import { api, apiRaw, uploadFile as uploadFileHelper } from "../../api/client";
import { useFormFillStore } from "../../stores/form-fill-store";

interface Props {
  question: QuestionSnapshot;
  /**
   * type:"file" sorularda cevap değeri = clean attachment_id listesi. Verildiğinde
   * her refresh'te clean id'ler onChange ile senkronlanır. Kanıt belgesi (evidence)
   * modunda undefined bırakılır — cevap değerine dokunulmaz.
   */
  syncValue?: { value: string[]; onChange: (v: string[]) => void };
}

interface PendingUpload {
  id: string; // client-side temp id
  file: File;
  progress: "pending" | "uploading" | "scanning" | "error";
  errorMessage?: string;
}

export function AttachmentUploader({ question, syncValue }: Props): JSX.Element {
  const projectId = useFormFillStore((s) => s.projectId);
  const [attachments, setAttachments] = useState<AttachmentInfo[]>([]);
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [loading, setLoading] = useState(true);
  // Closure refs — useEffect dep'lerini sabit tutmak için
  const syncValueRef = useRef(syncValue);
  syncValueRef.current = syncValue;
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  const refresh = async (): Promise<void> => {
    if (!projectId) return;
    try {
      const res = await api<AttachmentListResponse>(
        `/projects/${projectId}/questions/${question.code}/attachments`,
      );
      setAttachments(res.attachments);
      const sync = syncValueRef.current;
      if (sync) {
        const cleanIds = res.attachments.filter((a) => a.scanStatus === "clean").map((a) => a.id);
        const current = new Set(sync.value);
        if (current.size !== cleanIds.length || cleanIds.some((id) => !current.has(id))) {
          sync.onChange(cleanIds);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // Poll: pending varsa 2.5sn'de bir refresh. attachmentsRef ile stale closure kaçın.
    const interval = setInterval(() => {
      const hasPending = attachmentsRef.current.some(
        (a) => a.scanStatus === "pending" || a.scanStatus === "error",
      );
      if (hasPending) void refresh();
    }, 2_500);
    return () => clearInterval(interval);
  }, [projectId, question.code]);

  const uploadFile = async (file: File): Promise<void> => {
    if (!projectId) return;
    const tempId = crypto.randomUUID();
    setPending((p) => [...p, { id: tempId, file, progress: "pending" }]);

    try {
      // 1) Presign
      const presignReq: AttachmentPresignRequest = {
        questionCode: question.code,
        fileName: file.name,
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
      };
      const presign = await api<AttachmentPresignResponse>(
        `/projects/${projectId}/attachments/presign`,
        { method: "POST", body: presignReq },
      );

      setPending((p) =>
        p.map((x) => (x.id === tempId ? { ...x, progress: "uploading" as const } : x)),
      );

      // 2) Backend proxy PUT (CSRF token + auth cookie otomatik)
      await uploadFileHelper(presign.uploadUrl, file);

      // 3) Commit (BE scan worker'ı tetikler)
      setPending((p) =>
        p.map((x) => (x.id === tempId ? { ...x, progress: "scanning" as const } : x)),
      );
      await apiRaw(`/projects/${projectId}/attachments/${presign.attachmentId}/commit`, {
        method: "POST",
      });

      // 4) Listeden client pending'ı sil, BE listesini yeniden çek
      setPending((p) => p.filter((x) => x.id !== tempId));
      await refresh();
    } catch (err) {
      setPending((p) =>
        p.map((x) =>
          x.id === tempId
            ? {
                ...x,
                progress: "error" as const,
                errorMessage: err instanceof Error ? err.message : "Yükleme başarısız",
              }
            : x,
        ),
      );
    }
  };

  const deleteAttachment = async (attId: string, fileName: string): Promise<void> => {
    if (!projectId) return;
    if (!window.confirm(`"${fileName}" dosyası silinecek. Emin misiniz?`)) return;
    const res = await apiRaw(`/projects/${projectId}/attachments/${attId}`, { method: "DELETE" });
    if (res.ok || res.status === 204) {
      await refresh();
    }
  };

  const onDrop = (e: React.DragEvent<HTMLLabelElement>): void => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    for (const f of files) void uploadFile(f);
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(e.target.files ?? []);
    for (const f of files) void uploadFile(f);
    e.target.value = ""; // aynı dosyayı tekrar seçmeye izin ver
  };

  return (
    <div>
      <label
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="rounded-lg p-4 text-center cursor-pointer"
        style={{
          background: "var(--color-bg)",
          border: "2px dashed var(--color-border)",
          color: "var(--color-text-3)",
          display: "block",
        }}
      >
        <input type="file" multiple className="hidden" onChange={onPick} />
        <div style={{ color: "var(--color-text-2)", fontSize: "0.88rem", fontWeight: 500 }}>
          Dosya seçmek için tıklayın veya sürükleyip bırakın
        </div>
        <div style={{ fontSize: "0.75rem", marginTop: 4 }}>
          PDF, görsel, ofis belgeleri · max 50MB
        </div>
      </label>

      {loading && (
        <p className="mt-3" style={{ color: "var(--color-text-3)", fontSize: "0.8rem" }}>
          Yüklenen dosyalar kontrol ediliyor…
        </p>
      )}

      {(attachments.length > 0 || pending.length > 0) && (
        <ul className="mt-3 flex flex-col gap-2">
          {pending.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between px-3 py-2 rounded-lg"
              style={{
                background: "var(--color-surface)",
                border: "1px solid var(--color-border)",
              }}
            >
              <div className="min-w-0">
                <div
                  style={{
                    color: "var(--color-text)",
                    fontSize: "0.85rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {p.file.name}
                </div>
                <div style={{ color: "var(--color-text-3)", fontSize: "0.72rem" }}>
                  {formatBytes(p.file.size)} ·{" "}
                  {p.progress === "uploading"
                    ? "yükleniyor…"
                    : p.progress === "scanning"
                      ? "tarama bekleniyor…"
                      : p.progress === "error"
                        ? (p.errorMessage ?? "hata")
                        : "hazırlanıyor…"}
                </div>
              </div>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background:
                    p.progress === "error" ? "var(--color-error)" : "var(--color-warning)",
                }}
              />
            </li>
          ))}
          {attachments.map((a) => (
            <li
              key={a.id}
              className="flex items-center justify-between px-3 py-2 rounded-lg gap-3"
              style={{
                background: "var(--color-surface)",
                border: "1px solid var(--color-border)",
              }}
            >
              <div className="min-w-0 flex-1">
                <a
                  href={a.downloadUrl}
                  download={a.fileName}
                  title={`"${a.fileName}" indir`}
                  style={{
                    color: "var(--color-text)",
                    fontSize: "0.85rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                    textDecoration: "none",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = "underline";
                    e.currentTarget.style.color = "var(--color-accent)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = "none";
                    e.currentTarget.style.color = "var(--color-text)";
                  }}
                >
                  {a.fileName}
                </a>
                <div style={{ color: "var(--color-text-3)", fontSize: "0.72rem" }}>
                  {formatBytes(a.sizeBytes)} ·{" "}
                  {a.purgedAt ? "saklama süresi doldu — silindi" : scanLabel(a.scanStatus)}
                </div>
              </div>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: a.purgedAt ? "var(--color-text-3)" : scanColor(a.scanStatus),
                }}
              />
              <button
                onClick={() => void deleteAttachment(a.id, a.fileName)}
                aria-label={`${a.fileName} dosyasını sil`}
                title="Dosyayı sil"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--color-text-3)",
                  fontSize: "1.1rem",
                  cursor: "pointer",
                  padding: "0 4px",
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function scanLabel(s: AttachmentInfo["scanStatus"]): string {
  switch (s) {
    case "pending":
      return "tarama bekleniyor…";
    case "clean":
      return "temiz ✓";
    case "infected":
      return "virüs tespit edildi";
    case "quarantined":
      return "karantinada";
    case "error":
      return "tarama hatası — yeniden denenecek";
  }
}

function scanColor(s: AttachmentInfo["scanStatus"]): string {
  switch (s) {
    case "pending":
      return "var(--color-warning)";
    case "clean":
      return "var(--color-success)";
    case "infected":
    case "quarantined":
    case "error":
      return "var(--color-error)";
  }
}
